import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Users, 
  User, 
  DollarSign, 
  Heart, 
  Calendar,
  Download,
  Eye,
  TrendingUp,
  UserCheck,
  Gift,
  Coins,
  RefreshCw,
  ChevronRight,
  X,
  CheckCircle,
  Clock
} from 'lucide-react';

const AllContributionsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    search: ''
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Charger les données
  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/dev/contributions/all-members-contributions', {
        params: filters
      });
      
      if (response.success) {
        setData(response.data);
      } else {
        console.error('Erreur API:', response.message);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMemberDetails = async (memberId) => {
    setLoadingDetails(true);
    try {
      const response = await apiService.get(`/dev/contributions/member/${memberId}/contributions`, {
        params: { year: filters.year }
      });
      
      if (response.success) {
        setMemberDetails(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' XOF';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (status) => {
    const styles = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirme': 'bg-green-100 text-green-800',
      'en_attente': 'bg-yellow-100 text-yellow-800'
    };
    
    const labels = {
      'paid': 'Payé',
      'pending': 'En attente',
      'confirme': 'Confirmé',
      'en_attente': 'En attente'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const MemberCard = ({ member }) => (
    <div className="bg-white rounded-lg shadow-md p-4 border hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className={`p-2 rounded-full ${member.is_couple ? 'bg-purple-100' : 'bg-blue-100'}`}>
            {member.is_couple ? 
              <Users className="w-4 h-4 text-purple-600" /> : 
              <User className="w-4 h-4 text-blue-600" />
            }
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{member.full_name}</h3>
            {member.couple_name && (
              <p className="text-sm text-purple-600">👫 {member.couple_name}</p>
            )}
            <p className="text-xs text-gray-500">{member.email}</p>
          </div>
        </div>
        
        <div className="text-right">
          <span className={`px-2 py-1 rounded-full text-xs ${member.status === 'actif' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
            {member.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-green-50 rounded p-2 text-center">
          <div className="flex items-center justify-center mb-1">
            <Coins className="w-3 h-3 text-green-600 mr-1" />
            <span className="text-xs text-green-600">Cotisations</span>
          </div>
          <p className="text-sm font-bold text-green-800">{formatAmount(member.cotisations.total_amount)}</p>
          <p className="text-xs text-gray-600">{member.cotisations.paid_count}/{member.cotisations.total_count} payées</p>
        </div>
        
        <div className="bg-pink-50 rounded p-2 text-center">
          <div className="flex items-center justify-center mb-1">
            <Gift className="w-3 h-3 text-pink-600 mr-1" />
            <span className="text-xs text-pink-600">Dons</span>
          </div>
          <p className="text-sm font-bold text-pink-800">{formatAmount(member.dons.total_amount)}</p>
          <p className="text-xs text-gray-600">{member.dons.count} don(s)</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t">
        <div>
          <span className="text-xs text-gray-500">Total: </span>
          <span className="font-bold text-indigo-600">{formatAmount(member.totals.contributions_and_dons)}</span>
        </div>
        
        <button
          onClick={() => {
            setSelectedMember(member);
            loadMemberDetails(member.id);
          }}
          className="flex items-center px-3 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Eye className="w-3 h-3 mr-1" />
          Voir détails
        </button>
      </div>
    </div>
  );

  const CoupleCard = ({ couple }) => (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-md p-4 border border-purple-200">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="p-2 rounded-full bg-purple-100">
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{couple.couple_name}</h3>
            <p className="text-sm text-gray-600">{couple.members.length} membre(s)</p>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-lg font-bold text-purple-700">{formatAmount(couple.total_contributions)}</p>
          <p className="text-xs text-gray-500">Total couple</p>
        </div>
      </div>

      <div className="space-y-2 mb-3">
        {couple.members.map((member, index) => (
          <div key={member.id} className="flex items-center justify-between bg-white bg-opacity-50 rounded p-2">
            <span className="text-sm font-medium">{member.full_name}</span>
            <div className="flex space-x-1">
              <button
                onClick={() => {
                  setSelectedMember(member);
                  loadMemberDetails(member.id);
                }}
                className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
              >
                Détails
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="text-center bg-white bg-opacity-50 rounded p-2">
          <p className="text-xs text-gray-600">Cotisations</p>
          <p className="font-semibold text-green-700">{formatAmount(couple.total_cotisations)}</p>
        </div>
        <div className="text-center bg-white bg-opacity-50 rounded p-2">
          <p className="text-xs text-gray-600">Dons</p>
          <p className="font-semibold text-pink-700">{formatAmount(couple.total_dons)}</p>
        </div>
      </div>
    </div>
  );

  // Modal des détails
  const DetailsModal = () => {
    if (!selectedMember || !memberDetails) return null;

    const { member, cotisations, dons, couple, summary } = memberDetails;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* En-tête */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Contributions de {member.name}</h2>
              {couple && <p className="text-purple-600">👫 Couple: {couple.couple_name}</p>}
            </div>
            <button
              onClick={() => {
                setSelectedMember(null);
                setMemberDetails(null);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loadingDetails ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p>Chargement des détails...</p>
            </div>
          ) : (
            <div className="p-6">
              {/* Résumé */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Cotisations</p>
                  <p className="text-xl font-bold text-green-700">{formatAmount(summary.total_cotisations)}</p>
                  <p className="text-xs text-gray-500">{summary.cotisations_paid} payées, {summary.cotisations_pending} en attente</p>
                </div>
                
                <div className="bg-pink-50 rounded-lg p-4 text-center">
                  <Heart className="w-8 h-8 text-pink-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Dons</p>
                  <p className="text-xl font-bold text-pink-700">{formatAmount(summary.total_dons)}</p>
                  <p className="text-xs text-gray-500">{dons.length} don(s)</p>
                </div>
                
                <div className="bg-indigo-50 rounded-lg p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-xl font-bold text-indigo-700">
                    {formatAmount(summary.total_cotisations + summary.total_dons)}
                  </p>
                  <p className="text-xs text-gray-500">Année {summary.year}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cotisations */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Coins className="w-5 h-5 mr-2 text-green-600" />
                    Cotisations ({cotisations.length})
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {cotisations.length > 0 ? cotisations.map((cotisation) => (
                      <div key={cotisation.id} className="bg-gray-50 rounded p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{cotisation.period_month}/{cotisation.period_year}</p>
                          <p className="text-sm text-gray-500">
                            {cotisation.payment_date ? formatDate(cotisation.payment_date) : 'Non payé'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatAmount(cotisation.amount)}</p>
                          {getStatusBadge(cotisation.status)}
                        </div>
                      </div>
                    )) : (
                      <p className="text-gray-500 text-sm">Aucune cotisation pour {summary.year}</p>
                    )}
                  </div>
                </div>

                {/* Dons */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Gift className="w-5 h-5 mr-2 text-pink-600" />
                    Dons ({dons.length})
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {dons.length > 0 ? dons.map((don) => (
                      <div key={don.id} className="bg-pink-50 rounded p-3">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-sm">{don.campaign}</p>
                          <p className="font-bold text-pink-700">{formatAmount(don.amount)}</p>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{formatDate(don.created_at)}</span>
                          {getStatusBadge(don.status)}
                        </div>
                        {don.message && (
                          <p className="text-xs text-gray-600 mt-1 italic">"{don.message}"</p>
                        )}
                      </div>
                    )) : (
                      <p className="text-gray-500 text-sm">Aucun don pour {summary.year}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Informations du couple */}
              {couple && couple.other_members.length > 0 && (
                <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">👫 Autres membres du couple</h3>
                  <div className="space-y-2">
                    {couple.other_members.map((otherMember) => (
                      <div key={otherMember.id} className="flex items-center justify-between bg-white rounded p-2">
                        <span className="text-sm">{otherMember.name}</span>
                        <div className="text-xs text-gray-600">
                          {otherMember.contributions_count} cotisations • {formatAmount(otherMember.contributions_total)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des membres et contributions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50">
      <div className="container mx-auto px-4 py-8">
        {/* En-tête */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div className="flex items-center mb-4 lg:mb-0">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 hover:bg-white hover:bg-opacity-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Membres & Contributions
              </h1>
              <p className="text-gray-600 mt-1">
                Liste complète des membres avec leurs contributions et dons
              </p>
            </div>
          </div>
          
          {/* Boutons d'action */}
          <div className="flex space-x-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Recherche */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher par nom, email ou couple..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Filtre année */}
            <select
              value={filters.year}
              onChange={(e) => setFilters({...filters, year: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {data && (
          <>
            {/* Statistiques globales */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Membres</p>
                    <p className="text-2xl font-bold text-gray-900">{data.stats.total_members}</p>
                  </div>
                  <Users className="w-8 h-8 text-indigo-600" />
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Couples</p>
                    <p className="text-2xl font-bold text-purple-700">{data.stats.total_couples}</p>
                  </div>
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Individuels</p>
                    <p className="text-2xl font-bold text-blue-700">{data.stats.total_individuals}</p>
                  </div>
                  <User className="w-8 h-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Année</p>
                    <p className="text-2xl font-bold text-indigo-700">{data.stats.year}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="space-y-8">
              {/* Couples */}
              {data.couples.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Users className="w-6 h-6 mr-2 text-purple-600" />
                    Couples ({data.couples.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {data.couples.map((couple, index) => (
                      <CoupleCard key={index} couple={couple} />
                    ))}
                  </div>
                </div>
              )}

              {/* Membres individuels */}
              {data.individuals.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <User className="w-6 h-6 mr-2 text-blue-600" />
                    Membres Individuels ({data.individuals.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {data.individuals.map((member) => (
                      <MemberCard key={member.id} member={member} />
                    ))}
                  </div>
                </div>
              )}

              {/* Message si aucune donnée */}
              {data.members.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-24 h-24 mx-auto mb-4 text-gray-300">
                    <Search className="w-full h-full" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun membre trouvé</h3>
                  <p className="text-gray-500">
                    Essayez de modifier vos filtres ou vérifiez qu'il y a des membres enregistrés.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Modal des détails */}
        <DetailsModal />
      </div>
    </div>
  );
};

export default AllContributionsPage;